/**
 * Report-share API: public read-only links to a brand's reports (the published
 * Brand Brain and finished competitor comparisons). The brand-level twin of
 * `../campaign-detail/api/shareGrants.ts`, with the same two transports:
 *
 *   - list/mint/update/revoke are AUTHED admin operations through `apiClient`
 *     (`/api/v1/brands/{brandId}/report-shares`, brand:share:* — admin roles only).
 *   - `fetchPublicReportView` / `unlockPublicReportView` hit the PUBLIC
 *     `/share-view/report/{token}` endpoint at the API root with a raw `fetch`
 *     (no Bearer token, no `/api/v1`), `credentials: 'include'` for the
 *     password unlock cookie. `apiBase` is passed in by the page.
 *
 * Wire shapes mirror internal/reportshare (grants_handler.go grantDTO,
 * view_handler.go publicReportView). A 404 reuses `ShareViewNotFoundError` and
 * a failed unlock `UnlockShareViewError`, so both public pages read errors the
 * same way.
 */
import { apiClient } from '@discovr/common';
import type { BrandBrain, DataResponse } from '@discovr/types';
import { ShareViewNotFoundError, UnlockShareViewError, type ShareGrantTtl } from '../campaign-detail/api/shareGrants';

export type ReportShareKind = 'brand_brain' | 'comparison';

export interface ReportShareGrantDto {
  id: string;
  kind: ReportShareKind;
  /** Set for `comparison` links only. */
  comparison_id?: string;
  /** The public link (brand app `/r/{token}`). Re-minting the report replaces it. */
  url: string;
  created_at: string;
  expires_at: string;
  revoked_at?: string;
  status: 'active' | 'expired' | 'revoked';
  click_count: number;
  last_viewed_at?: string;
  password_protected: boolean;
}

export interface MintReportShareInput {
  kind: ReportShareKind;
  comparison_id?: string;
  ttl: ShareGrantTtl;
  /** 4-72 chars; sets the link's password. */
  password?: string;
  /** Clears a password the report's previous link had. With neither this nor `password`, a
   *  re-mint keeps the stored password (so rotating a protected link never makes it public). */
  remove_password?: boolean;
}

export interface UpdateReportShareInput {
  ttl: ShareGrantTtl;
  password?: string;
  remove_password?: boolean;
}

const sharesPath = (brandId: string) => `/brands/${encodeURIComponent(brandId)}/report-shares`;

/** GET /api/v1/brands/{brandId}/report-shares — every link ever minted for the brand, newest first. */
export async function listReportShares(brandId: string): Promise<ReportShareGrantDto[]> {
  const res = await apiClient.get<DataResponse<ReportShareGrantDto[]>>(sharesPath(brandId));
  return res.data.data;
}

/** POST /api/v1/brands/{brandId}/report-shares — 409 when there is nothing to share yet. */
export async function mintReportShare(brandId: string, input: MintReportShareInput): Promise<ReportShareGrantDto> {
  const res = await apiClient.post<DataResponse<ReportShareGrantDto>>(sharesPath(brandId), input);
  return res.data.data;
}

/** PATCH /api/v1/brands/{brandId}/report-shares/{grantId} — new expiry (from now) and password change. */
export async function updateReportShare(brandId: string, grantId: string, input: UpdateReportShareInput): Promise<void> {
  await apiClient.patch(`${sharesPath(brandId)}/${encodeURIComponent(grantId)}`, input);
}

/** DELETE /api/v1/brands/{brandId}/report-shares/{grantId} — the link stops working at once. */
export async function revokeReportShare(brandId: string, grantId: string): Promise<void> {
  await apiClient.delete(`${sharesPath(brandId)}/${encodeURIComponent(grantId)}`);
}

// ─── Public (anonymous) report view ─────────────────────────────────────────

/** The shared report. Exactly one of `brand_brain` / `comparison` is set, matching `kind`. */
export interface PublicReportView {
  kind: ReportShareKind;
  brand: { name: string; logo_url?: string };
  grant: { expires_at: string };
  /**
   * The latest confirmed version as a public projection (Go reportshare/brandbrain_public.go):
   * no brand_id/rev/page_hashes or run bookkeeping; evidence limited to public web citations, with
   * `hidden_sources` counting what was dropped per field; and the private creator rules
   * (`topics.rules.never_work_with`, `rival_blocklist`, `rival_deal`) replaced by a
   * {@link RedactedRule} marker. The page turns those into its locked placeholders.
   */
  brand_brain?: Omit<BrandBrain, 'brand_id' | 'rev' | 'page_hashes'>;
  /** The comparison document minus its brand id, shortlist and run internals; @discovr/ui's
   *  `toPublicComparison` fills those back in with empty values. */
  comparison?: Record<string, unknown>;
}

/** What a shared Brand Brain sends in place of a private rule's value. */
export interface RedactedRule {
  redacted: true;
  /** How many entries the hidden rule holds. */
  count: number;
}

export type PublicReportViewResult =
  | { kind: 'view'; data: PublicReportView }
  | { kind: 'password_required'; brandName: string };

interface PasswordRequiredWire {
  password_required: true;
  brand: { name: string };
}

function isPasswordRequired(d: unknown): d is PasswordRequiredWire {
  return typeof d === 'object' && d !== null && (d as { password_required?: unknown }).password_required === true;
}

function reportViewUrl(apiBase: string, token: string, suffix = ''): string {
  return `${apiBase.replace(/\/$/, '')}/share-view/report/${encodeURIComponent(token)}${suffix}`;
}

/** GET {apiBase}/share-view/report/{token}. Throws `ShareViewNotFoundError` on a 404 (every dead-link case). */
export async function fetchPublicReportView(apiBase: string, token: string): Promise<PublicReportViewResult> {
  const res = await fetch(reportViewUrl(apiBase, token), {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (res.status === 404) throw new ShareViewNotFoundError();
  if (!res.ok) throw new Error(`Report view request failed (${res.status})`);
  const body = (await res.json()) as { data: PublicReportView | PasswordRequiredWire };
  if (isPasswordRequired(body.data)) return { kind: 'password_required', brandName: body.data.brand.name };
  return { kind: 'view', data: body.data };
}

/** POST {apiBase}/share-view/report/{token}/unlock. Throws `UnlockShareViewError` with the BE's message on failure. */
export async function unlockPublicReportView(apiBase: string, token: string, password: string): Promise<PublicReportView> {
  const res = await fetch(reportViewUrl(apiBase, token, '/unlock'), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ password }),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw new UnlockShareViewError(err?.error?.message ?? `Unable to unlock this link (${res.status})`, res.status);
  }
  const body = (await res.json()) as { data: PublicReportView };
  return body.data;
}
